import { createContext, useContext, useMemo } from "react";
import { useAuth } from "./AuthContext";
import { Roles } from "../constants/permissions";

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const { user } = useAuth();

  const permissions = useMemo(() => {
    if (!user) return [];
    const perms = user.permissions || [];
    return perms.map((p) => (typeof p === "string" ? p : p.code || p.name));
  }, [user]);

  const roles = useMemo(() => {
    if (!user) return [];
    if (user.role && typeof user.role === "string") {
      return [user.role];
    }
    const r = user.roles || [];
    return r.map((role) =>
      typeof role === "string" ? role : role.name || role.role,
    );
  }, [user]);

  const hasPermission = (permission, options = { logic: "AND" }) => {
    if (!permission) return false;
    if (roles.includes(Roles.SUPER_ADMIN)) return true;

    const logic = options?.logic || "AND";
    const requiredPermissions = Array.isArray(permission)
      ? permission
      : [permission];

    if (logic === "OR") {
      return requiredPermissions.some((p) => permissions.includes(p));
    }
    return requiredPermissions.every((p) => permissions.includes(p));
  };

  const isSuperAdmin = roles.includes(Roles.SUPER_ADMIN);

  const value = {
    permissions,
    roles,
    hasPermission,
    isSuperAdmin,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
};

export const Can = ({ permission, logic = "AND", children, fallback = null }) => {
  const { hasPermission } = usePermissions();
  const allowed = permission ? hasPermission(permission, { logic }) : true;
  return allowed ? children : fallback;
};
